import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, Send, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "@/api";

export default function SmtpTestPanel() {
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    if (!to.includes("@")) {
      toast.error("Inserisci un indirizzo email valido");
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const { data } = await api.post("/auth/admin/test-smtp", { to });
      setResult(data);
      if (data.success) {
        toast.success("Email di prova inviata");
      } else {
        toast.error(data.error || "Invio fallito — controlla i log");
      }
    } catch (err) {
      const data = err.response?.data;
      setResult(data || { success: false, error: err.message });
      toast.error(data?.detail || data?.error || "Errore durante l'invio");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <div className="p-3 border-b border-slate-200 flex items-center gap-2 text-sm font-semibold text-slate-700">
        <Mail className="w-4 h-4 text-slate-500" />
        Diagnostica SMTP · invia email di prova
      </div>
      <div className="p-4 space-y-3 text-sm">
        <p className="text-slate-600">
          Verifica se la configurazione SMTP nel file <code className="px-1 py-0.5 bg-slate-100 rounded">backend/.env</code> funziona.
          L'email di test viene inviata a un indirizzo reale usando <code className="px-1 py-0.5 bg-slate-100 rounded">SMTP_HOST</code>,
          <code className="px-1 py-0.5 bg-slate-100 rounded"> SMTP_PORT</code> e <code className="px-1 py-0.5 bg-slate-100 rounded">SMTP_SECURITY</code>
          {" "}(ssl / starttls / none). Nessuna credenziale viene esposta.
        </p>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <input
            type="email"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="destinatario@esempio.it"
            data-testid="smtp-test-input"
            className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm"
          />
          <button
            onClick={send}
            disabled={sending}
            data-testid="smtp-test-send"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold rounded-sm disabled:opacity-60"
          >
            <Send className="w-4 h-4" />
            {sending ? "Invio in corso..." : "Invia email SMTP di prova"}
          </button>
        </div>
        {result && (
          <div
            data-testid="smtp-test-result"
            className={`p-3 rounded-sm border text-xs font-mono ${
              result.success
                ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                : "bg-rose-50 border-rose-200 text-rose-900"
            }`}
          >
            <div className="flex items-center gap-2 mb-1 font-sans font-semibold">
              {result.success ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
              {result.success ? "Invio riuscito" : "Invio fallito"}
              {result.code ? <span className="opacity-70"> · SMTP code {result.code}</span> : null}
            </div>
            {result.error && <div className="whitespace-pre-wrap">{result.error}</div>}
            {result.config && (
              <div className="mt-2 opacity-80">
                host={result.config.host || "—"} · port={result.config.port} · security={result.config.security}
                · from={result.config.from || "—"} · to={result.config.to_masked}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
